<script setup lang="ts">
// Source-of-edge stack (spec 2026-10-30 §7): under a fund's Growth of 1 on Risk → Live vs backtest, the rule's
// payoff split into stale pricing / continuation / drift / unexplained over the rolling window, as a daily history,
// with the payoff and buy-and-hold lines on top. Values are bp/day from the API, shown as % per year.
import { computed, ref, watch } from 'vue'
import { api, type EdgePoint, type EdgeSourceResponse } from '../../api/client'
import RiskStackedAreaChart from '../RiskStackedAreaChart.vue'
import { ANNUAL } from './edgeMapGeometry'
import { pctYear, readingLine } from './edgeSourceFormat'

const props = defineProps<{ fundId: string; window: 120 | 250 }>()
const emit = defineEmits<{ (e: 'window', w: 120 | 250): void }>()
const WINDOWS: readonly (120 | 250)[] = [120, 250]

const YEAR_ROWS = 250
const FLAT_PCT = 2 // a change under 2 %/yr reads as flat

const data = ref<EdgeSourceResponse | null>(null)
const error = ref<string | null>(null)
const loading = ref(false)
let seq = 0

async function load() {
  const mine = ++seq
  loading.value = true
  try {
    const r = await api.edgeSource(props.fundId, props.window)
    if (mine !== seq) return
    data.value = r
    error.value = null
  } catch (e) {
    if (mine !== seq) return
    data.value = null
    error.value = e instanceof Error ? e.message : 'unavailable'
  } finally {
    if (mine === seq) loading.value = false
  }
}
watch(() => [props.fundId, props.window], load, { immediate: true })

const series = computed<EdgePoint[]>(() => data.value?.series ?? [])
const toPct = (get: (p: EdgePoint) => number) => series.value.map(p => ({ time: p.asOf, value: get(p) * ANNUAL }))

const bands = computed(() => [
  { id: 'stale', label: 'Stale pricing', color: '#4caf50', data: toPct(p => p.staleBp) },
  { id: 'continuation', label: 'Continuation', color: '#58a6ff', data: toPct(p => p.continuationBp) },
  { id: 'drift', label: 'Drift', color: '#9e9e9e', data: toPct(p => p.driftBp) },
  { id: 'unexplained', label: 'Unexplained', color: '#6d6d6d', data: toPct(p => p.unexplainedBp) },
])
const lines = computed(() => [
  { id: 'payoff', label: 'Rule payoff', color: '#e6edf3', data: toPct(p => p.payoffBp), lineWidth: 2 as const },
  { id: 'bh', label: 'Buy & hold', color: '#aaaaaa', data: toPct(p => p.bhBp), dashed: true },
])
const markers = computed(() => (data.value?.markers ?? []).map(m => ({ time: m.date, text: m.label, muted: true })))

const tiles = computed(() => {
  const s = series.value
  if (!s.length) return []
  const now = s[s.length - 1]
  const ago = s.length > YEAR_ROWS ? s[s.length - 1 - YEAR_ROWS] : null
  const tile = (key: string, title: string, get: (p: EdgePoint) => number) => {
    const d = ago ? (get(now) - get(ago)) * ANNUAL : null
    return {
      key, title, value: pctYear(get(now)), ago: ago ? `1y ago ${pctYear(get(ago))}` : null,
      cls: d == null ? 'fl' : d > FLAT_PCT ? 'up' : d < -FLAT_PCT ? 'dn' : 'fl',
      delta: d == null ? '' : `${d > 0 ? '+' : ''}${Math.round(d)} %/yr`,
    }
  }
  return [
    tile('stale', 'Stale-pricing return', p => p.staleBp),
    tile('continuation', 'Continuation', p => p.continuationBp),
    tile('payoff', 'Rule payoff', p => p.payoffBp),
  ]
})
const reading = computed(() => readingLine(series.value))
</script>

<template>
  <div class="ess" data-testid="edge-stack">
    <div class="ess-head">
      <h3 class="ess-title">Source of edge — rolling {{ window }} decision days, % per year</h3>
      <span class="ess-ctl">Window
        <button v-for="w in WINDOWS" :key="w" type="button" class="ess-btn" :class="{ on: w === window }"
          :data-testid="`edge-stack-window-${w}`" @click="emit('window', w)">{{ w }}d</button>
      </span>
    </div>
    <p v-if="error" class="ess-error" data-testid="edge-stack-error">Source of edge unavailable ({{ error }}).</p>
    <p v-else-if="loading && !data" class="ess-note">Loading…</p>
    <p v-else-if="!series.length" class="ess-note" data-testid="edge-stack-empty">
      No history yet for this fund — the nightly edge-source job fills it after its first run.</p>
    <template v-else>
      <div class="ess-tiles">
        <div v-for="t in tiles" :key="t.key" class="ess-tile" :data-testid="`edge-tile-${t.key}`">
          <div class="ess-tile-title">{{ t.title }}</div>
          <div class="ess-tile-value">{{ t.value }}</div>
          <div class="ess-tile-sub"><template v-if="t.ago">{{ t.ago }} · </template><span :class="t.cls">{{ t.delta || 'no year-ago point' }}</span></div>
        </div>
      </div>
      <div data-testid="edge-stack-chart">
        <RiskStackedAreaChart :bands="bands" :lines="lines" :markers="markers" format="pct0" />
      </div>
      <p v-if="reading" class="ess-reading" data-testid="edge-reading">{{ reading }}</p>
    </template>
  </div>
</template>

<style scoped>
.ess { margin-top: 1.25rem; }
.ess-head { display: flex; flex-wrap: wrap; align-items: center; gap: 0.5rem 1rem; margin-bottom: 0.5rem; }
.ess-title { margin: 0; font-size: 14px; color: #ccc; }
.ess-ctl { display: inline-flex; gap: 6px; align-items: center; font-size: 12px; color: #888; }
.ess-btn { padding: 2px 10px; border: 1px solid #333; border-radius: 10px; color: #999; background: transparent; cursor: pointer; font: inherit; }
.ess-btn.on { border-color: #58a6ff; background: #2a3f55; color: #e6edf3; }
.ess-tiles { display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); gap: 0.5rem; margin-bottom: 0.5rem; }
.ess-tile { background: #141414; border: 1px solid #2a2a2a; border-radius: 6px; padding: 0.5rem 0.7rem; }
.ess-tile-title { color: #888; font-size: 11px; text-transform: uppercase; letter-spacing: 0.04em; }
.ess-tile-value { color: #e6edf3; font-size: 18px; font-weight: 600; }
.ess-tile-sub { color: #888; font-size: 11px; }
.up { color: #4caf50; }
.dn { color: #f44336; }
.fl { color: #999; }
.ess-reading { color: #ccc; font-size: 12px; margin: 0.4rem 0 0; }
.ess-note { color: #888; font-size: 12px; }
.ess-error { color: #f44336; font-size: 12px; }
</style>
